import { useState, useEffect } from 'react';
import Login from './components/Login.jsx';
import Quiz from './components/Quiz.jsx';
import Confirmation from './components/Confirmation.jsx';
import { APP_CONFIG } from './config.js';
import AdminLogin from './components/AdminLogin.jsx';
import QualificationsPage from './components/QualificationsPage.jsx';
import UnitsListPage from './components/UnitsListPage.jsx';
import AdminRouter from './AdminRouter.jsx';
import { getUser, getAdminUser, clearSession, clearAdminSession, clearStudentSession, invalidateAdminCache } from './api.js';
import { getCurrentQualification, getTodayMidnight } from './utils/date.js';

const STAGES = { LOGIN: 'login', QUALIFICATIONS: 'qualifications', UNITS: 'units', QUIZ: 'quiz', DONE: 'done' };

export default function App() {
  const [portal, setPortal] = useState(() => {
    return new URLSearchParams(window.location.search).get('portal') === 'admin' ? 'admin' : 'student';
  });
  const [user, setUser] = useState(null);
  const [stage, setStage] = useState(STAGES.LOGIN);
  const [result, setResult] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedQualification, setSelectedQualification] = useState(null);
  // Staff-only override: opens the credential prompt inside Quiz. `done` hides
  // the header button once answers are already revealed for this attempt.
  const [adminAssist, setAdminAssist] = useState({ open: false, done: false });

  useEffect(() => {
    if (portal === 'admin') {
      const existing = getAdminUser();
      if (existing) {
        setUser(existing);
        window.location.replace('#/dashboard');
      }
    } else {
      const existing = getUser();
      if (existing) {
        setUser(existing);
        // Check if user has qualifications data
        if (Array.isArray(existing.qualifications)) {
          // Restore last stage from sessionStorage, or start at qualifications selection
          try {
            const savedStage = sessionStorage.getItem('ibma_assessment_stage');
            const savedUnit = sessionStorage.getItem('ibma_assessment_unit');
            const savedQualName = sessionStorage.getItem('ibma_assessment_qualification');

            if (savedStage === 'quiz' && savedUnit) {
              // Was in quiz with a unit selected - restore and go to quiz
              if (savedQualName) {
                const found = existing.qualifications.find(q => q.name === savedQualName);
                if (found) setSelectedQualification(found);
              }
              setStage(STAGES.QUIZ);
           } else if (savedStage === 'done') {
              // Restore qualification context for retry
              const savedUnit = sessionStorage.getItem('ibma_assessment_unit');
              const savedQualName = sessionStorage.getItem('ibma_assessment_qualification');

              if (savedQualName && existing.qualifications) {
                const found = existing.qualifications.find(q => q.name === savedQualName);
                if (found) {
                  setSelectedQualification(found);
                  setStage(STAGES.UNITS);
                } else {
                  // Saved qual no longer exists — fall through to computed current below
                  const current = getCurrentQualification(existing.qualifications, getTodayMidnight());
                  if (current) {
                    setSelectedQualification(current);
                    try { sessionStorage.setItem('ibma_assessment_qualification', current.name); } catch {}
                    setStage(STAGES.UNITS);
                  } else {
                    setStage(STAGES.QUALIFICATIONS);
                  }
                }
              } else if (savedUnit) {
                // No qual name but has unit — go to quiz directly
                setStage(STAGES.QUIZ);
              } else {
                const current = getCurrentQualification(existing.qualifications, getTodayMidnight());
                if (current) {
                  setSelectedQualification(current);
                  try { sessionStorage.setItem('ibma_assessment_qualification', current.name); } catch {}
                  setStage(STAGES.UNITS);
                } else {
                  setStage(STAGES.QUALIFICATIONS);
                }
              }
           } else if (savedQualName) {
              // Find the saved qualification by name
              const found = existing.qualifications.find(q => q.name === savedQualName);
              if (found) {
                setSelectedQualification(found);
                setStage(STAGES.UNITS);
              } else {
                // Saved qual no longer exists — fall through to computed current
                const current = getCurrentQualification(existing.qualifications, getTodayMidnight());
                if (current) {
                  setSelectedQualification(current);
                  try { sessionStorage.setItem('ibma_assessment_qualification', current.name); } catch {}
                  setStage(STAGES.UNITS);
                } else {
                  setStage(STAGES.QUALIFICATIONS);
                }
              }
            } else {
              // No saved qual — compute current by dates, or show all for manual selection
              const current = getCurrentQualification(existing.qualifications, getTodayMidnight());
              if (current) {
                setSelectedQualification(current);
                try { sessionStorage.setItem('ibma_assessment_qualification', current.name); } catch {}
                setStage(STAGES.UNITS);
              } else {
                setStage(STAGES.QUALIFICATIONS);
              }
            }
          } catch {
            setStage(STAGES.QUALIFICATIONS);
          }
        } else {
          setStage(STAGES.QUIZ);
        }
      }
    }
  }, [portal]);

  function switchPortal(newPortal) {
    // Clear only the session of the portal we're leaving (not both)
    if (portal === 'admin' && getAdminUser()) {
      clearAdminSession();
      invalidateAdminCache();
    } else if (portal === 'student' && getUser()) clearStudentSession();

    setStage(STAGES.LOGIN);
    setUser(null);
    setResult(null);
    setSelectedQualification(null);
    setAdminAssist({ open: false, done: false });
    try { sessionStorage.removeItem('ibma_assessment_unit'); } catch {}
    try { sessionStorage.removeItem('ibma_assessment_qualification'); } catch {}
    try { sessionStorage.removeItem('ibma_assessment_stage'); } catch {}
    setPortal(newPortal);
    const params = new URLSearchParams(window.location.search);
    if (newPortal === 'admin') {
      params.set('portal', 'admin');
    } else {
      params.delete('portal');
    }
    window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
  }

  function handleLogout() {
    if (portal === 'admin') clearAdminSession();
    else clearStudentSession();
    try { sessionStorage.removeItem('ibma_assessment_unit'); } catch {}
    setUser(null);
    setResult(null);
    setSelectedQualification(null);
    setAdminAssist({ open: false, done: false });
    setStage(STAGES.LOGIN);
  }

  function handleRetry() {
    setResult(null);
    setAdminAssist({ open: false, done: false });
    setReloadKey((k) => k + 1);
    try {
      const savedUnit = sessionStorage.getItem('ibma_assessment_unit');
      const savedQualName = sessionStorage.getItem('ibma_assessment_qualification');
      
      if (savedUnit) {
        // Coming from a quiz — go directly back to the same unit's quiz
        if (savedQualName && user?.qualifications) {
          const found = user.qualifications.find(q => q.name === savedQualName);
          if (found) setSelectedQualification(found);
        }
        setStage(STAGES.QUIZ);
      } else if (selectedQualification || savedQualName) {
        // Coming from a qualification flow without a specific unit — go to units list
        if (savedQualName && user?.qualifications) {
          const found = user.qualifications.find(q => q.name === savedQualName);
          if (found) setSelectedQualification(found);
        }
        setStage(STAGES.UNITS);
      } else {
        setStage(STAGES.QUIZ);
      }
    } catch {
      setStage(STAGES.QUIZ);
    }
  }

  function handleSelectQualification(qual) {
    setSelectedQualification(qual);
    try {
      sessionStorage.setItem('ibma_assessment_qualification', qual.name);
      sessionStorage.setItem('ibma_assessment_stage', 'units');
    } catch {}
    setStage(STAGES.UNITS);
  }

  function handleOpenUnit(unit) {
    // Store selected unit code for Quiz to pick up as course context
    try {
      sessionStorage.setItem('ibma_assessment_unit', unit.code);
      sessionStorage.setItem('ibma_assessment_stage', 'quiz');
    } catch {}
    setAdminAssist({ open: false, done: false });
    setStage(STAGES.QUIZ);
  }

  function handleBackToUnits() {
    // Clear quiz context and go back to units list
    try { sessionStorage.removeItem('ibma_assessment_unit'); } catch {}
    setAdminAssist({ open: false, done: false });
    if (selectedQualification) {
      setStage(STAGES.UNITS);
    } else {
      setStage(STAGES.QUALIFICATIONS);
    }
  }

  // Student portal — shown when logged in as student or on quiz/done stages
  if (portal === 'student' && stage !== STAGES.LOGIN) {
    return (
      <div className="container">
        {/* Persistent header banner */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '12px 0',
          marginBottom: 8,
          borderBottom: `1px solid var(--border)`
        }}>
          <h1 style={{ margin: 0, fontSize: 18 }}>{APP_CONFIG.APP_TITLE}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {stage === STAGES.QUIZ && !adminAssist.done && (
              <button
                onClick={() => setAdminAssist((s) => ({ ...s, open: !s.open }))}
                style={{ background: 'transparent', color: '#5b6776', border: 'none', padding: '4px 12px', cursor: 'pointer', fontSize: 13 }}
              >
                Admin Assistance
              </button>
            )}
            <button onClick={handleLogout} style={{ background: 'transparent', color: '#5b6776', border: 'none', padding: '4px 12px', cursor: 'pointer', fontSize: 13 }}>Sign out</button>
          </div>
        </div>

        {stage === STAGES.QUALIFICATIONS && user && (
          <QualificationsPage user={user} onSelect={handleSelectQualification} />
        )}

        {stage === STAGES.UNITS && selectedQualification && user && (
          <UnitsListPage
            qualification={selectedQualification}
            user={user}
            onOpenUnit={handleOpenUnit}
            onSelectQualification={handleSelectQualification}
          />
        )}

        {stage === STAGES.QUIZ && user && (
          <Quiz
            user={user}
            course={selectedQualification?.name}
            reloadKey={reloadKey}
            onSubmitted={(r) => { setResult(r); setStage(STAGES.DONE); }}
            onBackToUnits={handleBackToUnits}
            adminAssistOpen={adminAssist.open}
            onAdminAssistClose={() => setAdminAssist((s) => ({ ...s, open: false }))}
            onAdminAssistRevealed={() => setAdminAssist({ open: false, done: true })}
          />
        )}
        {stage === STAGES.DONE && user && (
          <Confirmation 
            user={user} 
            result={result} 
            onRetry={handleRetry} 
            onBackToUnits={handleBackToUnits}
          />
        )}
      </div>
    );
  }

 // Admin portal — shown when logged in as admin
  if (portal === 'admin' && user) {
    return (
      <AdminRouter onLogout={() => switchPortal('student')} adminUser={user} onAdminLogin={(u) => { setUser(u); }} />
    );
  }

  // Login screen — shared entry point for both portals
  return (
    <div className="container">
      <div className="header">
        <h1>{APP_CONFIG.APP_TITLE}{portal === 'admin' ? ' \u2014 Admin' : ''}</h1>
      </div>

      {portal === 'student' && (
        <>
          <div style={{ textAlign: 'right', marginBottom: 16 }}>
            <button onClick={() => switchPortal('admin')} style={{ background: 'transparent', color: 'var(--muted)', padding: '4px 0', fontSize: '13px', fontWeight: 'normal' }}>
              Switch to Admin Portal &rarr;
            </button>
          </div>
          <Login onLogin={(u) => { setUser(u); setStage(STAGES.QUALIFICATIONS); }} />
        </>
      )}

      {portal === 'admin' && (
        <>
          <div style={{ textAlign: 'right', marginBottom: 16 }}>
            <button onClick={() => switchPortal('student')} style={{ background: 'transparent', color: 'var(--muted)', padding: '4px 0', fontSize: '13px', fontWeight: 'normal' }}>
              Switch to Student Portal &rarr;
            </button>
          </div>
          <AdminLogin onLogin={(u) => { setUser(u); window.location.replace('#/dashboard'); }} />
        </>
      )}
    </div>
  );
}
